import { useLingui } from '@lingui/react/macro';
import { useEffect } from 'react';
import { createPortal } from 'react-dom';

import { usePortalContainer } from '@/api/evo';
import { getConfigOrNull } from '@/lib/config/config-registry';
import { useUser } from '@/lib/config/use-user';

import { FeatureFeedback } from './feature-feedback';

/**
 * Post-commit feedback (F3 / D28). Shown once after a user
 * successfully commits a rota (`onCommit` from the wizard's
 * activate flow). Adds a "once per generation" guard on top of
 * the OC component's own `reDisplayAfterDays` so the user can't
 * see the same feedback prompt again for the same generation by
 * re-opening the page within the cooldown window.
 *
 * Renders nothing while the host user is unauthenticated, when the
 * feedback has already been shown for this `generationId`, or when the
 * host hasn't supplied `feedback` config — otherwise `FeatureFeedback`
 * itself renders nothing but this component's backdrop still would,
 * leaving an empty dark overlay with nothing to dismiss it.
 *
 * Portalled to `document.body` (or the host's configured portal
 * container) rather than rendered inline: it's positioned with
 * `fixed`, which is trapped by the nearest ancestor that establishes a
 * containing block (e.g. any `transform`), not necessarily the
 * viewport. Left inline, a host page (e.g. WFM) can end up rendering it
 * in the wrong place instead of centred over the whole page.
 */
export function PostCommitFeedback({ generationId, show }: PostCommitFeedbackProps) {
  const { t } = useLingui();
  const user = useUser();
  const portalContainer = usePortalContainer();
  const feedbackConfigured = Boolean(getConfigOrNull()?.feedback);
  const storageKey = `feedback-shown-${generationId}`;
  const alreadyShown = Boolean(sessionStorage.getItem(storageKey));

  useEffect(() => {
    if (!show || alreadyShown) return;
    sessionStorage.setItem(storageKey, 'true');
  }, [show, alreadyShown, storageKey]);

  if (!show || !user || !feedbackConfigured || alreadyShown) return null;

  return createPortal(
    <div className="fixed top-1/2 left-1/2 z-[10000] -translate-x-1/2 -translate-y-1/2 rounded-lg bg-black/30 p-8">
      <FeatureFeedback
        feature="RotaTemplates-Activate"
        product="RotareadyTemplateBuilder"
        customerName={user.organisation.id}
        userId={user.id}
        theme="light"
        reDisplayAfterDays={30}
        label={t`How was publishing this template?`}
      />
    </div>,
    portalContainer ?? document.body,
  );
}

interface PostCommitFeedbackProps {
  generationId: string;
  show: boolean;
}
